import React, { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { motion, AnimatePresence } from 'motion/react';
import { User, Lock, UserPlus, Eye, EyeOff, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { GoogleSignInButton } from './GoogleSignInButton';
import { GoogleUsernameModal } from './GoogleUsernameModal';
import { Input } from './ui/input';
import afroYapLogo from '../assets/afroyap_full_logo.png';
const afroYapLogoBlue = afroYapLogo;
import { ParallaxBackground } from './utils/parallax';
import { useTheme } from './ThemeProvider';
import { AuthService } from '../services/api';
import { toast } from 'sonner';
import { auth } from '../services/firebaseConfig';
import { sendPasswordResetEmail } from 'firebase/auth';
import { AccountRecoveryModal } from './AccountRecoveryModal';
import { friendlyAuthError, authErrorCode } from '../utils/authErrors';

interface LoginScreenProps {
  onLogin: () => void;
  onSignup: () => void;
  onSignupComplete: () => void;
}

export function LoginScreen({ onLogin, onSignup, onSignupComplete }: LoginScreenProps) {
  const { theme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(false);
  // Google signup: profile held between the Google step and the username step.
  const [googleProfile, setGoogleProfile] = useState<any>(null);
  const [error, setError] = useState('');
  const [showRecovery, setShowRecovery] = useState(false);

  // 🛡️ SECURITY: Clear any stale login intent when entering or leaving the login flow
  useEffect(() => {
    const clearIntent = async () => {
      try {
        const { SafeStorage } = await import('../utils/safeStorage');
        SafeStorage.removeItem('login_intent_username');
      } catch (e) {}
    };
    
    clearIntent();
    return () => { clearIntent(); };
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await AuthService.loginWithIdentifier(email.trim(), password);
      onLogin();
    } catch (err: any) {
      // Mapped centrally so an unrecognised code can never fall through to
      // Firebase's own text. The `else if (err.message)` fallback that used to
      // live here is what put "Firebase: Error (auth/invalid-credential)." in
      // front of people who had simply mistyped their password.
      const message = friendlyAuthError(err, 'Login failed. Please check your credentials.');

      // Try trimmed password as a final recovery
      const code = authErrorCode(err);
      if ((code === 'auth/wrong-password' || code === 'auth/invalid-credential') && password !== password.trim()) {
        try {
          await AuthService.loginWithIdentifier(email.trim(), password.trim());
          onLogin();
          return;
        } catch {
          // Recovery also failed, show original error
        }
      }

      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] flex items-center justify-center p-6 relative overflow-y-auto">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>

      <motion.div
        className="relative z-10 w-full max-w-[340px] flex flex-col items-center py-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {/* LOGO */}
        <img
          src={afroYapLogo as any}
          alt="AfroYap"
          className="h-auto mb-16 drop-shadow-md object-contain"
          style={{ width: '100px' }}
        />

        {/* CREATE ACCOUNT BUTTON */}
        <Button
          onClick={onSignup}
          disabled={loading || socialLoading}
          className="w-full max-w-[280px] mx-auto h-12 rounded-xl font-bold shadow-lg flex items-center justify-center gap-2 mb-6 text-base text-white border-none"
          style={{
            backgroundImage: theme === 'dark'
              ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
              : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)'
          }}
        >
          <UserPlus className="w-5 h-5" />
          <span>Create Account</span>
        </Button>

        {/* OR DIVIDER */}
        <div className="w-full max-w-[280px] mx-auto flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-black/10 dark:bg-white/10" />
          <span className="text-sm font-bold text-zinc-800 dark:text-zinc-200">or</span>
          <div className="flex-1 h-px bg-black/10 dark:bg-white/10" />
        </div>

        {/* GOOGLE SIGN-IN */}
        <div className="w-full mb-6">
          <GoogleSignInButton
            disabled={loading}
            onLoadingChange={setSocialLoading}
            onNeedsUsername={(p) => setGoogleProfile(p)}
            onSignedIn={() => onLogin()}
          />
        </div>

        <GoogleUsernameModal
          open={!!googleProfile}
          profile={googleProfile}
          onComplete={() => { setGoogleProfile(null); onSignupComplete(); }}
          onCancel={async () => {
            setGoogleProfile(null);
            // Leave no half-made session behind: the Firebase user exists but has
            // no AfroYap document yet, which would strand them on the setup gate.
            try { await AuthService.logout(); } catch { /* noop */ }
          }}
        />

        {/* FORM */}
        <form onSubmit={handleLogin} className="w-full space-y-4">
          <div className="relative w-full max-w-[280px] mx-auto">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <User className="w-5 h-5 text-zinc-500" />
            </div>
            <Input
              type="text"
              placeholder="Username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading || socialLoading}
              autoCapitalize="none"
              autoCorrect="off"
              className="w-full h-12 pl-12 pr-4 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl text-sm placeholder:text-zinc-400 focus:border-[#F39C12] dark:focus:border-[#3B82F6] transition-all"
              style={{
                backgroundImage: theme === 'dark'
                  ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 100%)'
                  : 'linear-gradient(135deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 100%)'
              }}
            />
          </div>

          <div className="relative w-full max-w-[280px] mx-auto">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <Lock className="w-5 h-5 text-zinc-500" />
            </div>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading || socialLoading}
              className="w-full h-12 pl-12 pr-12 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-xl text-sm placeholder:text-zinc-400 focus:border-[#F39C12] dark:focus:border-[#3B82F6] transition-all"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-700"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {/* Forgot password link */}
          <div className="w-full max-w-[280px] mx-auto text-center mt-1">
            <button
              type="button"
              onClick={() => setShowRecovery(true)}
              className="text-xs font-medium underline-offset-2 hover:underline transition-all"
              style={{ color: theme === 'dark' ? 'rgba(148,163,184,0.8)' : 'rgba(82,82,91,0.85)' }}
            >
              Forgot your password?
            </button>
          </div>

          <Button
            type="submit"
            disabled={loading || socialLoading}
            className="w-full max-w-[280px] mx-auto h-12 rounded-xl font-bold shadow-md transition-all flex items-center justify-center gap-2 text-base text-white border-none"
            style={{
              backgroundImage: theme === 'dark'
                ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                : 'linear-gradient(135deg, #F39C12 0%, #E67E22 100%)'
            }}
          >
            <LogIn className="w-5 h-5" />
            <span>{loading ? 'Logging in...' : 'Log In'}</span>
          </Button>
        </form>

        {/* APP STORE DOWNLOAD — web only.
            Inside the installed iOS app this invited the user to download the
            app they are already using, and App Review treats a link out to the
            store as a dead end. Platform-checked rather than deleted, because
            the web login screen still needs it. */}
        {!Capacitor.isNativePlatform() && (
        <a 
          href="https://apps.apple.com/app/apple-store/id6757343172?pt=128420251&ct=TikTok%20Users&mt=8"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full max-w-[280px] mx-auto mt-6"
        >
          <Button
            variant="outline"
            className="w-full h-12 rounded-xl font-bold border-2 border-black/5 dark:border-white/10 bg-white/50 dark:bg-white/5 backdrop-blur-md hover:bg-white/80 dark:hover:bg-white/10 transition-all flex items-center justify-center gap-3 text-sm text-black dark:text-white"
          >
            <svg viewBox="0 0 384 512" className="w-5 h-5 fill-current">
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 33-17.9 64.4-17.9 31.7 0 38.3 17.9 64.2 17.4 48.5-.7 85.7-82.2 102.6-119.3-22.6-14.9-42.1-39.6-43-69.6zM263.1 81.2c15.5-18.5 25.5-44.9 22.2-71.2-22.6 1.1-49.6 15.2-65.8 34-14.6 16.6-27.3 42.7-23.4 68.2 25.2 2 49.5-13.5 67-31z"/>
            </svg>
            <span>Download on App Store</span>
          </Button>
        </a>
        )}



        {/* TERMS */}
        <div className="mt-8 text-center text-[11px] text-zinc-600 dark:text-zinc-400 px-4 leading-relaxed font-normal">
          By continuing, you agree to our{' '}
          <button onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: { screen: 'legal', type: 'terms' } }))} className="underline font-medium">Terms of Service</button>
          {' '}and{' '}
          <button onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: { screen: 'legal', type: 'privacy' } }))} className="underline font-medium">Privacy Policy</button>
        </div>

        {error && (
          <p className="mt-4 text-xs text-red-500 font-bold text-center bg-red-50 dark:bg-red-900/20 p-3 rounded-xl">
            {error}
          </p>
        )}
      </motion.div>

      {/* Account Recovery Modal */}
      <AnimatePresence>
        {showRecovery && (
          <AccountRecoveryModal onClose={() => setShowRecovery(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}
